import React, { Suspense, lazy, useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom'
import { Upload, Home, User, Settings, Menu, X, Search, Users, MapPin, Package, FileText, Crown, ShoppingCart, Building2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { videoAPI } from './lib/api'
import { AuthProvider, useAuth } from './contexts/UserContext'
import ProtectedRoute from './components/Auth/ProtectedRoute'
import TopBarAuthButtons from './components/TopBarAuthButtons'
import Navigation from './components/Navigation'

// App 主入口组件
const HomePage = lazy(() => import('./pages/HomePage'))
const VideoUploadPage = lazy(() => import('./pages/VideoUploadPage'))
const VideoPlayerPage = lazy(() => import('./pages/VideoPlayerPage'))
const VideoManagementPage = lazy(() => import('./pages/VideoManagementPage'))
const HelpPage = lazy(() => import('./pages/HelpPage'))
const ContactPage = lazy(() => import('./pages/ContactPage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const RegisterPage = lazy(() => import('./pages/RegisterPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'))
const MembershipCenterPage = lazy(() => import('./pages/MembershipCenterPage'))
const CoachesPage = lazy(() => import('./pages/CoachesPage'))
const CoachDetailPage = lazy(() => import('./pages/CoachDetailPage'))
const CoachManagementPage = lazy(() => import('./pages/CoachManagementPage'))
const CourtsPage = lazy(() => import('./pages/CourtsPage'))
const CourtDetailPage = lazy(() => import('./pages/CourtDetailPage'))
const CourtManagementPage = lazy(() => import('./pages/CourtManagementPage'))
const ProductsPage = lazy(() => import('./pages/ProductsPage'))
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'))
const ProductManagementPage = lazy(() => import('./pages/ProductManagementPage'))
const ProductEditPage = lazy(() => import('./pages/ProductEditPage'))
const ProductCreatePageSimple = lazy(() => import('./pages/ProductCreatePageSimple'))
const ProductCreatePage = lazy(() => import('./pages/ProductCreatePage'))
const CartPage = lazy(() => import('./pages/CartPage'))
const AdminPage = lazy(() => import('./pages/AdminPage'))
const NewsPage = lazy(() => import('./pages/NewsPage'))
const NewsDetailPage = lazy(() => import('./pages/NewsDetailPage'))
const NewsManagementPage = lazy(() => import('./pages/NewsManagementPage'))
const NewsFormPage = lazy(() => import('./pages/NewsFormPage'))
const UserDashboardPage = lazy(() => import('./pages/UserDashboardPage'))
const ClubsPage = lazy(() => import('./pages/ClubsPage'))
const ClubDetailPage = lazy(() => import('./pages/ClubDetailPage'))
const ClubManagementPage = lazy(() => import('./pages/ClubManagementPage'))
const VideoListPage = lazy(() => import('./pages/VideoListPage'))

// 灵动网球
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-tennis-200 border-t-tennis-600 rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600">搜索中...</p>
    </div>
  </div>
)

// 搜索组件
const SearchBar = () => {
  const [query, setQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)

  const handleSearch = async () => {
    if (!query.trim()) return
    
    setIsSearching(true)
    try {
      const results = await videoAPI.searchVideos(query)
      console.log('搜索结果:', results)
      // TODO: 显示搜索结果
    } catch (error) {
      toast.error('搜索失败')
    } finally {
      setIsSearching(false)
    }
  }

  return (
    <div className="relative max-w-md mx-auto">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="搜索网球视频..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tennis-500 focus:border-transparent"
        />
        <button
          type="button"
          onClick={handleSearch}
          className="absolute left-3 top-2.5 w-5 h-5 text-gray-400 hover:text-gray-600 focus:outline-none"
          aria-label="搜索"
        >
          <Search className="w-5 h-5" />
        </button>
        {isSearching && (
          <div className="absolute right-3 top-2.5">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-tennis-600 rounded-full animate-spin"></div>
          </div>
        )}
      </div>
    </div>
  )
}


// 购物车数量角标组件
const CartBadge = () => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    const updateCount = () => {
      try {
        const cart = JSON.parse(localStorage.getItem('tennis_cart') || '[]')
        setCount(Array.isArray(cart) ? cart.reduce((sum: number, item: any) => sum + (item.quantity || 1), 0) : 0)
      } catch {
        setCount(0)
      }
    }

    updateCount()
    window.addEventListener('cartUpdated', updateCount)
    return () => window.removeEventListener('cartUpdated', updateCount)
  }, [])

  if (count === 0) return null

  return (
    <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
      {count > 99 ? '99+' : count}
    </span>
  )
}

function App() {
  const [systemStatus, setSystemStatus] = useState<any>(null);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const status = await videoAPI.getSystemStatus();
        setSystemStatus(status);
      } catch (error) {
        console.error('Failed to fetch system status:', error);
      }
    };
    checkStatus();
  }, []);

  // useLocation needs to be inside the Router context
  return (
    <AuthProvider>
      <Router>
        <MainApp systemStatus={systemStatus} />
      </Router>
    </AuthProvider>
  );
}

// Inner component that has access to useLocation (inside Router)
function MainApp({ systemStatus }) {
  const location = useLocation();
  const isVideoPlayer = location.pathname.startsWith('/video/');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await videoAPI.getCategories();
        setCategories(data?.categories || (Array.isArray(data) ? data : []));
      } catch (error) {
        console.warn('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar - hide on video player */}
      {!isVideoPlayer && (
        <header className="sticky top-0 z-30 bg-white shadow-sm">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <a href="/" className="flex items-center space-x-2">
                  <img src="/logo/tennis-logo.svg" alt="Logo" className="w-8 h-8" />
                  <span className="text-xl font-bold text-gray-900 hidden md:inline">灵动网球</span>
                </a>
                <SearchBar />
              </div>
              <div className="flex items-center space-x-4">
                {systemStatus && (
                  <div className="hidden md:flex items-center space-x-2 text-sm">
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-gray-600">在线</span>
                    </div>
                    <span className="text-gray-400">|</span>
                    <span className="text-gray-600">{systemStatus.stats?.videos || systemStatus.video_count || 0} 个视频</span>
                  </div>
                )}
                <div className="hidden md:flex items-center space-x-4"><TopBarAuthButtons /></div>
                {/* 购物车图标 - 带数量角标 */}
                <a
                  href="/cart"
                  className="relative p-2 text-gray-600 hover:text-tennis-600 transition-colors"
                  title="购物车"
                >
                  <ShoppingCart size={20} />
                  <CartBadge />
                </a>
                <a href="/upload" className="btn-primary flex items-center space-x-2"><Upload size={18} /><span>上传视频</span></a>
              </div>
            </div>
          </div>
        </header>
      )}
      <div className="flex">
        {!isVideoPlayer && (
          <div className="hidden md:block"><Navigation /></div>
        )}
        <main className="flex-1">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/upload" element={<ProtectedRoute><VideoUploadPage /></ProtectedRoute>} />
              <Route path="/video/:id" element={<VideoPlayerPage />} />
              <Route path="/video" element={<VideoListPage />} />
              <Route path="/admin" element={<ProtectedRoute requireAdmin={true}><VideoManagementPage /></ProtectedRoute>} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/membership" element={<ProtectedRoute><MembershipCenterPage /></ProtectedRoute>} />
              <Route path="/coaches" element={<CoachesPage />} />
              <Route path="/coaches/:id" element={<CoachDetailPage />} />
              <Route path="/admin/coaches" element={<ProtectedRoute requireAdmin={true}><CoachManagementPage /></ProtectedRoute>} />
              <Route path="/courts" element={<CourtsPage />} />
              <Route path="/courts/:id" element={<CourtDetailPage />} />
              <Route path="/admin/courts" element={<ProtectedRoute requireAdmin={true}><CourtManagementPage /></ProtectedRoute>} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/:id" element={<ProductDetailPage />} />
              <Route path="/news" element={<NewsPage />} />
              <Route path="/news/:id" element={<NewsDetailPage />} />
              <Route path="/admin/news" element={<ProtectedRoute requireAdmin={true}><NewsManagementPage /></ProtectedRoute>} />
              <Route path="/admin/news/create" element={<ProtectedRoute requireAdmin={true}><NewsFormPage /></ProtectedRoute>} />
              <Route path="/admin/news/edit/:id" element={<ProtectedRoute requireAdmin={true}><NewsFormPage /></ProtectedRoute>} />
              <Route path="/admin/products" element={<ProtectedRoute requireAdmin={true}><ProductManagementPage /></ProtectedRoute>} />
              <Route path="/admin/products/create" element={<ProtectedRoute requireAdmin={true}><ProductCreatePage /></ProtectedRoute>} />
              <Route path="/admin/products/edit/:id" element={<ProtectedRoute requireAdmin={true}><ProductEditPage /></ProtectedRoute>} />
              <Route path="/clubs" element={<ClubsPage />} />
              <Route path="/clubs/:id" element={<ClubDetailPage />} />
              <Route path="/admin/clubs" element={<ProtectedRoute requireAdmin={true}><ClubManagementPage /></ProtectedRoute>} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/user/dashboard" element={<ProtectedRoute><UserDashboardPage /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute requireAdmin={true}><AdminPage /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
      </div>
      {!isVideoPlayer && (
        <footer className="bg-gray-900 text-white mt-12">
          <div className="container mx-auto px-4 py-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <img src="/logo/tennis-logo.svg" alt="Logo" className="w-8 h-8" />
                  <span className="text-xl font-bold">灵动网球</span>
                </div>
                <p className="text-gray-400">专业网球视频平台</p>
              </div>
              <div>
                <h4 className="font-semibold mb-4">视频分类</h4>
                <ul className="space-y-2 text-gray-400">
                  <li><a href="/?category=1" className="hover:text-white">比赛集锦</a></li>
                  <li><a href="/?category=2" className="hover:text-white">教学视频</a></li>
                  <li><a href="/?category=3" className="hover:text-white">技术分析</a></li>
                  <li><a href="/?category=4" className="hover:text-white">球员专访</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-4">快速链接</h4>
                <ul className="space-y-2 text-gray-400">
                  <li><a href="/upload" className="hover:text-white">上传视频</a></li>
                  <li><a href="/help" className="hover:text-white">使用帮助</a></li>
                  <li><a href="/contact" className="hover:text-white">联系我们</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-4">联系我们</h4>
                <ul className="space-y-2 text-gray-400">
                  <li>邮箱: contact@lingdong-tennis.com</li>
                  <li>电话: 400-123-4567</li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-800 mt-8 pt-6 text-center text-gray-500 text-sm">
              <p>© 2026 灵动网球视频平台. 保留所有权利.</p>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}
export default App
